import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import {
	CheckCircleIcon,
	InformationCircleIcon,
	LockClosedIcon,
	XCircleIcon,
} from "@heroicons/react/24/outline";
import { Button, Field, Input, Label, Switch } from "@headlessui/react";
import { handleEnterKey } from "../../app/keyboardUtils";
import { useAppSelector } from "../../app/hooks";
import { UserRole } from "../../app/userSlice";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import GradeSelector from "../../components/GradeSelector";
import { Loading } from "../../components/Loading";
import RequiredField from "../../components/RequiredField";
import Select from "../../components/input/Select";
import { SimpleOption } from "../../components/input/SimpleListbox";
import { GradeModel, ScoringLevelModel, useGetScoringLevelsQuery } from "../lookup/lookupApiSlice";
import { UserSchoolViewModel } from "../users/usersApiSlice";
import {
	AdminCreateCourseEditModel,
	useAddUserToCoursesMutation,
	useCreateCourseByAdminMutation,
	useLazyGetCreateCourseByAdminQuery,
} from "./coursesApiSlice";

interface AddCourseFormProps {
	schoolId?: number;
	userId?: number;
	activeSchools?: UserSchoolViewModel[];
	allSchoolCount?: number;
	districtOptions?: SimpleOption[];
	onClose: () => void;
	setErrorMessage?: (message: string) => void;
}

export default function AddCourseByAdminForm(props: AddCourseFormProps) {
	const { config } = useAppSelector((state) => state.site);
	const { role } = useAppSelector((state) => state.user);

	const navigate = useNavigate();

	const [data, setData] = useState<AdminCreateCourseEditModel>();

	const [schoolId, setSchoolId] = useState<number | undefined>(props.schoolId);
	const [districtId, setDistrictId] = useState<number>();
	const [name, setName] = useState("");
	const [grade, setGrade] = useState<GradeModel>();
	const [scoringLevel, setScoringLevel] = useState<ScoringLevelModel>();
	const [active, setActive] = useState(true);
	const [allowAvatars, setAllowAvatars] = useState(true);
	const [externalId, setExternalId] = useState("");
	const [outsideSiteId, setOutsideSiteId] = useState("");

	const [schoolOptions, setSchoolOptions] = useState<SimpleOption[]>([]);

	const [errorMessage, setErrorMessage] = useState("");

	const scoringLevelResult = useGetScoringLevelsQuery();
	const scoringLevels = scoringLevelResult.data;

	const [callInitCreateCourse] = useLazyGetCreateCourseByAdminQuery();
	const [callCreateCourse] = useCreateCourseByAdminMutation();
	const [addUserToCourse] = useAddUserToCoursesMutation();

	useEffect(() => {
		if (!props.districtOptions) return;
		if (props.districtOptions.length > 0) {
			setDistrictId(Number(props.districtOptions[0].value));
		}
	}, [props.districtOptions]);

	useEffect(() => {
		if (!props.activeSchools) return;
		const options = props.activeSchools
			.filter((s) => s.districtID === districtId)
			.map((s) => ({ value: s.schoolID, label: s.name }) as SimpleOption);

		setSchoolOptions(options);

		if (options.length > 0) {
			setSchoolId(Number(options[0].value));
		}
	}, [districtId, props.activeSchools]);

	useEffect(() => {
		if (!schoolId) return;
		callInitCreateCourse(schoolId)
			.unwrap()
			.then((result) => setData(result));
	}, [schoolId]);

	const showOutsideSite =
		!config?.isUtahComposeSite && (data?.showCleverId || data?.showClassLinkId);

	const isValid = validate();

	function validate() {
		if (!data) return false;
		if (data.isDateRangeRequired) return false;
		if (name == "") return false;
		if (!grade) return false;

		return true;
	}

	function handleGradeChange(grade: GradeModel) {
		const level = scoringLevels?.find((s) => s.scoringLevelId === grade.scoringLevelId);
		setGrade(grade);
		setScoringLevel(level);
	}

	function handleSave() {
		if (!isValid || !data || !schoolId) return;
		const model: AdminCreateCourseEditModel = {
			...data,
			schoolId: schoolId,
			gradeId: grade?.gradeId!,
			name: name,
			externalID: externalId,
			active: active,
			disableUserImages:
				data.disableUserImagesByDistrict || data.disableUserImagesBySchool || !allowAvatars,
			classLinkId: data.showClassLinkId ? outsideSiteId : "",
			cleverId: data.showCleverId ? outsideSiteId : "",
		};

		callCreateCourse(model)
			.unwrap()
			.then((courseId) => {
				// If userId is provided (from AddCoursesToUser), add the user to the new course
				if (props.userId) {
					addUserToCourse({ userId: props.userId, courseIds: [Number(courseId)] })
						.unwrap()
						.then((errors) => {
							let errorMessage = "";
							if (errors && errors.length > 0) {
								errorMessage = `The following courses could not be added for the user: `;
								errors.map((error) => {
									errorMessage += `\n${error}`;
								});
								scrollTo(0, 0);
							}

							if (props.setErrorMessage) props.setErrorMessage(errorMessage);
							props.onClose();
						});
				} else {
					navigate(`/schools/course?courseId=${courseId}`);
				}
			})
			.catch((error: any) => {
				setErrorMessage(error.data);
			});
	}

	function handleCancel() {
		props.onClose();
	}

	return (
		<div>
			<Loading loading={!data}></Loading>
			{data && (
				<>
					<AlertMessage
						className="mt-2 text-sm"
						type={AlertLevel.Error}
						message={errorMessage}
						showIcon={true}
					/>
					{props.activeSchools?.find((s) => s.schoolID === schoolId)?.seatsRemaining ===
						0 && (
						<AlertMessage
							className="mt-2 text-sm"
							type={AlertLevel.Warning}
							message={
								"The school has 0 seats remaining. You may add a new course, however any student not already registered in a course in the school, will not be added."
							}
							showIcon={true}
						/>
					)}
					<p className="mt-1 text-xs leading-6 text-gray-600 text-right">
						<RequiredField className="!text-sm" /> - Denotes a required field
					</p>
					<div className="grid grid-cols-6 gap-3">
						{(props.districtOptions?.length ?? 0) > 1 && districtId != undefined && (
							<Field className="col-span-3">
								<Select
									value={districtId}
									setValue={(v) => setDistrictId(v as number)}
									label="District"
									simpleListBoxId="districtFilter"
									options={props.districtOptions ?? []}
									simpleListBoxClasses="w-full"
								/>
							</Field>
						)}
						{schoolOptions.length > 0 &&
							(props.allSchoolCount ?? 0) > 1 &&
							schoolId && (
								<Field
									className={
										(props.districtOptions?.length ?? 0) > 1
											? "col-span-3"
											: "col-span-full"
									}>
									<Select
										value={schoolId}
										setValue={(v) => setSchoolId(v as number)}
										label="School"
										simpleListBoxId="schoolFilter"
										options={schoolOptions}
										simpleListBoxClasses="w-full"
									/>
								</Field>
							)}
						<Field className="col-span-4">
							<div className="flex justify-between items-end">
								<Label htmlFor="course-name" className="font-medium text-sm">
									Course Name
									<RequiredField />
								</Label>
							</div>
							<Input
								type="text"
								id="course-name"
								className="w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
								onChange={(e) => setName(e.target.value)}></Input>
						</Field>
						<Field className="col-span-2">
							<Label htmlFor="activeSwitch" className="font-medium text-sm">
								Status
							</Label>
							<div className="mt-2">
								<div className="flex items-center ml-2">
									<Label className="sr-only">Activated Toggle</Label>
									<Switch
										checked={active}
										onChange={setActive}
										name="activeSwitch"
										className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
										<span
											aria-hidden="true"
											className="pointer-events-none inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
										/>
									</Switch>

									<div className="ml-2 flex gap-1 items-center text-sm">
										{active ? (
											<CheckCircleIcon className="size-7 text-green-500" />
										) : (
											<XCircleIcon className="size-7 text-red-500" />
										)}
										<p
											className={`font-semibold ${active ? "text-green-700" : "text-red-700"}`}>
											{active ? "Active" : "Inactive"}
										</p>
									</div>
								</div>
							</div>
						</Field>

						<Field className="col-span-3">
							<Label htmlFor="course-grade" className="font-medium text-sm">
								Grade
								<RequiredField />
							</Label>
							<GradeSelector
								grade={grade}
								onChange={handleGradeChange}
								className="block w-full mt-1"
							/>
						</Field>
						<Field className="col-span-3">
							<div className="flex items-center gap-x-2">
								<Label
									htmlFor="course-scoring-level"
									className="font-medium text-sm">
									Scoring Level
								</Label>
								<InformationCircleIcon
									data-tooltip-id="scoring-level-info"
									className="size-6 text-primary-700 cursor-pointer"
								/>
								<Tooltip id="scoring-level-info" place="bottom" className="z-50">
									<div className="w-80">
										Course scoring level is banded, based on the course grade.
										When a student is added to a course, they will be assigned
										the course scoring level, but a teacher may adjust it using
										the <strong>Edit Student</strong> action.
									</div>
								</Tooltip>
							</div>
							<Input
								type="text"
								id="scoring-level"
								disabled={true}
								readOnly={true}
								className="w-full mt-1 rounded-lg border-none bg-slate-200/75 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
								value={scoringLevel?.name ?? "--"}
							/>
						</Field>
						<Field className={`${showOutsideSite ? "col-span-3" : "col-span-full"}`}>
							<Label htmlFor="external-id" className="font-medium text-sm">
								External ID
							</Label>
							<Input
								type="text"
								name="external-id"
								id="external-id"
								className="w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
								value={externalId}
								onChange={(e) => setExternalId(e.target.value)}
							/>
						</Field>
						{role === UserRole.Admin && showOutsideSite && (
							<Field className="col-span-3">
								<Label htmlFor="classlinkClever" className="font-medium text-sm">
									{data.showCleverId ? "Clever" : "ClassLink"} ID
								</Label>
								<Input
									id="classlinkClever"
									name="classlinkClever"
									type="text"
									className="w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={outsideSiteId}
									onChange={(e) => setOutsideSiteId(e.target.value)}
								/>
							</Field>
						)}

						{!config?.isUtahComposeSite && (
							<Field className="col-span-full mt-1 flex items-center gap-x-6">
								<Label htmlFor="userAvatar" className="font-medium text-sm">
									Allow User Avatars
								</Label>
								{data.disableUserImagesByDistrict ||
								data.disableUserImagesBySchool ? (
									<div className="flex items-center">
										<Switch
											checked={false}
											disabled={true}
											name="userAvatar"
											className="group relative inline-flex items-center h-7 w-12 shrink-0 rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden">
											<span
												aria-hidden="true"
												className="pointer-events-none absolute inset-0 flex items-center justify-center size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5">
												<LockClosedIcon className="size-5 items-center text-gray-500" />
											</span>
										</Switch>
										<p className="ml-2 text-sm">
											{data.disableUserImagesByDistrict
												? "Disabled on the district level."
												: "Disabled on the school level."}
										</p>
									</div>
								) : (
									<Field className="flex items-center">
										<Label className="sr-only">User Avatar Toggle</Label>
										<Switch
											checked={allowAvatars}
											onChange={setAllowAvatars}
											name="userAvatar"
											className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
											<span
												aria-hidden="true"
												className="pointer-events-none inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
											/>
										</Switch>
									</Field>
								)}
							</Field>
						)}
					</div>
				</>
			)}

			<div className="mt-4 grid grid-cols-2 gap-3">
				<Button
					type="button"
					id="cancel-button"
					onClick={handleCancel}
					onKeyDown={(e) => handleEnterKey(e, handleCancel)}
					className="btn btn-neutral">
					Cancel
				</Button>
				<Button
					type="button"
					id="confirm-button"
					disabled={!isValid}
					onClick={handleSave}
					onKeyDown={(e) => handleEnterKey(e, handleSave)}
					className="btn btn-primary">
					Save
				</Button>
			</div>
		</div>
	);
}
